import { useState } from "react";
import { Shield, BarChart3, Target } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import type { CookiePreferences } from "@/types/cookieConsent";
import { toast } from "sonner";

interface CookiePreferencesProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: Partial<CookiePreferences>) => void;
  currentPreferences: CookiePreferences | null;
}

export function CookiePreferences({ isOpen, onClose, onSave, currentPreferences }: CookiePreferencesProps) {
  const [analytics, setAnalytics] = useState(currentPreferences?.analytics ?? true);
  const [marketing, setMarketing] = useState(currentPreferences?.marketing ?? true);

  const handleSave = () => {
    onSave({ analytics, marketing });
    toast.success("Your preferences have been saved");
  };

  const handleAcceptAll = () => {
    setAnalytics(true);
    setMarketing(true);
    onSave({ analytics: true, marketing: true });
    toast.success("All cookies accepted");
  };

  const handleRejectAll = () => {
    setAnalytics(false);
    setMarketing(false);
    onSave({ analytics: false, marketing: false });
    toast.success("Non-essential cookies rejected");
  };

  const cookieCategories = [
    {
      id: 'necessary',
      icon: Shield,
      name: 'Strictly Necessary',
      description: 'Essential cookies for site functionality. These cannot be disabled.',
      enabled: true,
      disabled: true,
      cookies: [
        {
          name: 'Session Cookies',
          purpose: 'Maintain your session and security',
          duration: 'Session',
          provider: 'Shore Sight Marine',
        },
      ],
    },
    {
      id: 'analytics',
      icon: BarChart3,
      name: 'Analytics & Performance',
      description: 'Help us understand how visitors interact with our website.',
      enabled: analytics,
      disabled: false,
      onToggle: setAnalytics,
      cookies: [
        {
          name: '_ga',
          purpose: 'Analytics tracking',
          duration: '2 years',
          provider: 'Google Analytics',
        },
        {
          name: '_gid',
          purpose: 'Session identification',
          duration: '24 hours',
          provider: 'Google Analytics',
        },
      ],
    },
    {
      id: 'marketing',
      icon: Target,
      name: 'Marketing & Advertising',
      description: 'Used for personalized ads and retargeting campaigns.',
      enabled: marketing,
      disabled: false,
      onToggle: setMarketing,
      cookies: [
        {
          name: '_fbp',
          purpose: 'Facebook pixel tracking',
          duration: '90 days',
          provider: 'Meta Platforms',
        },
        {
          name: '_gcl_au',
          purpose: 'Google Ads conversion tracking',
          duration: '90 days',
          provider: 'Google LLC',
        },
      ],
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Cookie Preferences</DialogTitle>
          <DialogDescription>
            Manage your cookie preferences. You can enable or disable different types of cookies below.
            Changes will take effect immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {cookieCategories.map((category) => {
            const Icon = category.icon;
            return (
              <div
                key={category.id}
                className="border border-border rounded-lg p-4 space-y-3 bg-card"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <Icon className="h-5 w-5 text-azure-500 mt-1 flex-shrink-0" />
                    <div className="space-y-1 flex-1">
                      <h3 className="font-semibold text-base">{category.name}</h3>
                      <p className="text-sm text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={category.enabled}
                    onCheckedChange={category.onToggle}
                    disabled={category.disabled}
                    aria-label={`Toggle ${category.name}`}
                  />
                </div>

                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="details" className="border-0">
                    <AccordionTrigger className="text-sm text-azure-500 hover:text-azure-600 py-2">
                      See cookie details
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        {category.cookies.map((cookie, idx) => (
                          <div
                            key={idx}
                            className="bg-muted/50 rounded-md p-3 space-y-1 text-sm"
                          >
                            <div className="font-medium">{cookie.name}</div>
                            <div className="text-muted-foreground">
                              <span className="font-medium">Purpose:</span> {cookie.purpose}
                            </div>
                            <div className="text-muted-foreground">
                              <span className="font-medium">Duration:</span> {cookie.duration}
                            </div>
                            <div className="text-muted-foreground">
                              <span className="font-medium">Provider:</span> {cookie.provider}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button
            onClick={handleRejectAll}
            variant="outline"
            className="flex-1"
          >
            Reject All
          </Button>
          <Button
            onClick={handleSave}
            variant="secondary"
            className="flex-1"
          >
            Save Preferences
          </Button>
          <Button
            onClick={handleAcceptAll}
            className="flex-1 bg-azure-500 hover:bg-azure-600"
          >
            Accept All
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center pt-2">
          California residents: We do not sell your personal information. Your preferences are saved locally
          and will be respected for 12 months.
        </p>
      </DialogContent>
    </Dialog>
  );
}
